import { handleSendConsultingRequest } from '@/entities/support';
import { useUserStore } from '@/entities/user';
import { useSetState } from '@/shared/lib/customHooks';
import { auth, generateFullName, showMessageError, showMessageSuccess } from '@/shared/lib';
import _ from 'lodash';
import { useLayoutEffect, useMemo } from 'react';
import { t } from 'ttag';
import { CONSULTING_REQUEST_TYPE, SUBJECT_CONSULTING_REQUEST } from '../../config/learnMoreModal';
import { ERROR_IP_SPAM, handleValidateData } from '../../lib/learnMoreModal';
import type { LearnMoreModalProps } from './learnMoreModal.types';

const INITIAL_DATA = {
  name: '',
  phoneNumber: '',
  contactEmail: '',
  facilityName: '',
};

/**
 * Runs the marketing Learn More consulting-request form for the chosen request type.
 * Holds the form fields and errors in local state, derives the title, description and subject from
 * the type, pushes the title through setTitle and prefills the name from auth, then validates and
 * sends the request with the signed-in user id and closes through onCancel.
 *
 * @param props - The onCancel and setTitle callbacks and the consulting-request type.
 * @returns The form state, the type's content and the field change and send handlers.
 */
export const useLearnMoreModal = ({
  onCancel,
  setTitle,
  type,
}: Pick<LearnMoreModalProps, 'onCancel' | 'setTitle' | 'type'>) => {
  /**
   * Reads the current user's id from the user store. Selects me?.id via useUserStore.
   *
   * @returns The logged-in user's id, if any.
   */
  const userId = useUserStore((s) => s.me?.id);

  const [state, setState] = useSetState({
    isSubmit: false,
    ...INITIAL_DATA,
    errors: {
      ...INITIAL_DATA,
    },
  });

  /**
   * Computes the modal's title, description and email subject for the current consulting-request type. Returns Bioflux Trade In or RPM/CCM copy via a switch on type and throws for an invalid type.
   *
   * @returns The title, description and subject for the selected type.
   */
  const content = useMemo(() => {
    switch (type) {
      case CONSULTING_REQUEST_TYPE.BIOFLUX_TRADE_IN: {
        return {
          title: t`Learn More About Bioflux Trade In`,
          description: t`Thank you for your interest. Please fill out the info below and we will contact you.`,
          subject: SUBJECT_CONSULTING_REQUEST.biofluxTradeIn,
        };
      }
      case CONSULTING_REQUEST_TYPE.RPM_CCM_SERVICE: {
        return {
          title: t`Learn more about RPM/CCM`,
          description: t`Thank you for your interest. Please fill out the info below and we will contact you.`,
          subject: SUBJECT_CONSULTING_REQUEST.rpmCcmService,
        };
      }
      default: {
        throw new Error('Invalid type');
      }
    }
  }, [type]);

  /**
   * Stores a Learn More form field value and clears the error left on it. Writes the value under its field name and blanks the matching entry in state.errors.
   *
   * @param name - The field that changed.
   * @param value - The new field value.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    setState({
      [name]: value,
      errors: {
        ...state.errors,
        [name]: '',
      },
    });
  };

  /**
   * Submits the marketing Learn More consulting request once the form validates. Resets the errors and flags submitting, writes the validation errors back and stops when handleValidateData reports any, otherwise posts name, email, phone, facility and the content subject plus the userId when signed in, then toasts success and closes, or toasts the error and closes on the IP-spam message.
   *
   * @returns A promise resolving once the request has been sent or rejected.
   */
  const onClickSend = async () => {
    setState({
      errors: {
        ...INITIAL_DATA,
      },
      isSubmit: true,
    });

    const errors = handleValidateData(state);

    if (!_.isEmpty(errors)) {
      setState({
        errors: {
          ...state.errors,
          ...errors,
        },
        isSubmit: false,
      });
      return;
    }

    try {
      const input = {
        fullname: state.name,
        email: state.contactEmail,
        phone: state.phoneNumber,
        subject: content.subject,
        facilityName: state.facilityName,
      };
      if (userId) {
        _.assign(input, { userId });
      }
      await handleSendConsultingRequest(input);
      showMessageSuccess({
        content: t`Thank you! We will contact you shortly`,
      });
      onCancel?.();
    } catch (error) {
      console.error('Failed to send consulting request: ', error);
      const message = error instanceof Error ? error.message : String(error);
      if (_.trim(message) === ERROR_IP_SPAM) {
        showMessageError({ content: ERROR_IP_SPAM });
        onCancel?.();
        return;
      }
      showMessageError({ content: message });
      setState({ isSubmit: false });
    }
  };

  useLayoutEffect(() => {
    setTitle?.(content.title);
    const userInfo = {
      firstName: auth.firstName(),
      lastName: auth.lastName(),
    };
    setState({ name: generateFullName(userInfo) });
  }, [content.title]);

  return {
    state,
    content,
    onChange,
    onClickSend,
  };
};
